import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { getAziende, getElencoConversioniCodici, eliminaMappatureConversioneMultiple } from "../api";

// Pagina dedicata (non più un riquadro dentro il pannello Impostazioni): con
// molte aziende/fornitori le corrispondenze "codice fornitore -> mio codice"
// possono diventare centinaia, e dentro il modulo azienda (una tab di un
// modale) non c'era spazio per filtrare comodamente e cancellare più righe
// insieme. Qui c'è tutta la pagina: filtro testo, filtro azienda (solo
// wtrendy, che vede tutte le aziende), selezione multipla ed eliminazione in
// blocco. Il permesso di vedere/cancellare resta quello di sempre (vedi
// templateConversioneVisibile in routes/ordini.js): un operatore normale
// vede solo le aziende a cui è assegnato, qui come ovunque.
const STILE_CAMPO = {
  width: "100%", padding: "9px 12px", borderRadius: "6px", border: "1px solid var(--cf-border-strong)",
  backgroundColor: "var(--cf-bg-card)", color: "var(--cf-text-primary)", fontSize: "13px", boxSizing: "border-box",
};

export default function ConversioneCodici() {
  const navigate = useNavigate();
  const location = useLocation();
  const { username } = useAuth();
  const eWtrendy = username === "wtrendy";

  const parametri = new URLSearchParams(location.search);
  const [azienda, setAzienda] = useState(parametri.get("azienda") || "");
  const [aziendeNote, setAziendeNote] = useState([]);
  const [elenco, setElenco] = useState(null); // null = non ancora caricato
  const [caricamento, setCaricamento] = useState(false);
  const [errore, setErrore] = useState("");
  const [filtro, setFiltro] = useState("");
  const [selezionate, setSelezionate] = useState(() => new Set()); // chiavi riga
  const [eliminando, setEliminando] = useState(false);

  useEffect(() => {
    if (eWtrendy) getAziende().then(setAziendeNote).catch(() => {});
  }, [eWtrendy]);

  function ricarica() {
    setCaricamento(true);
    setErrore("");
    getElencoConversioniCodici(eWtrendy ? azienda : undefined)
      .then(setElenco)
      .catch((e) => setErrore(e.message))
      .finally(() => setCaricamento(false));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(ricarica, [azienda, eWtrendy]);

  // Righe piatte (un'associazione per riga) da tutti i template/campi.
  const righe = useMemo(() => (elenco || []).flatMap((t) =>
    Object.entries(t.mappature || {}).flatMap(([campo, mappa]) =>
      Object.entries(mappa).map(([codice, mioCodice]) => ({
        chiave: `${t.templateId}|${campo}|${codice}`,
        templateId: t.templateId, cliente: t.cliente, campo, codice, mioCodice,
      }))
    )
  ), [elenco]);

  const testoFiltro = filtro.trim().toLowerCase();
  const righeFiltrate = useMemo(() => (
    testoFiltro
      ? righe.filter((r) => `${r.cliente} ${r.campo} ${r.codice} ${r.mioCodice}`.toLowerCase().includes(testoFiltro))
      : righe
  ), [righe, testoFiltro]);

  const tutteSelezionate = righeFiltrate.length > 0 && righeFiltrate.every((r) => selezionate.has(r.chiave));
  const qualcunaSelezionata = righeFiltrate.some((r) => selezionate.has(r.chiave));

  function toggleRiga(chiave) {
    setSelezionate((prev) => {
      const nuovo = new Set(prev);
      if (nuovo.has(chiave)) nuovo.delete(chiave); else nuovo.add(chiave);
      return nuovo;
    });
  }

  function toggleTutte() {
    setSelezionate((prev) => {
      const nuovo = new Set(prev);
      if (tutteSelezionate) {
        for (const r of righeFiltrate) nuovo.delete(r.chiave);
      } else {
        for (const r of righeFiltrate) nuovo.add(r.chiave);
      }
      return nuovo;
    });
  }

  async function eliminaSelezionate() {
    if (selezionate.size === 0) return;
    if (!window.confirm(`Togliere ${selezionate.size} corrispondenze selezionate? Al prossimo file, quei codici torneranno da risolvere.`)) return;
    setEliminando(true);
    setErrore("");
    try {
      const voci = righe.filter((r) => selezionate.has(r.chiave))
        .map((r) => ({ templateId: r.templateId, campo: r.campo, codice: r.codice }));
      await eliminaMappatureConversioneMultiple(voci);
      setSelezionate(new Set());
      ricarica();
    } catch (e) {
      setErrore(e.message);
    } finally {
      setEliminando(false);
    }
  }

  async function eliminaUna(r) {
    setEliminando(true);
    setErrore("");
    try {
      await eliminaMappatureConversioneMultiple([{ templateId: r.templateId, campo: r.campo, codice: r.codice }]);
      setSelezionate((prev) => { const n = new Set(prev); n.delete(r.chiave); return n; });
      ricarica();
    } catch (e) {
      setErrore(e.message);
    } finally {
      setEliminando(false);
    }
  }

  return (
    <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "19px", color: "var(--cf-text-primary)" }}>🔀 Conversione codici</h2>
          <p style={{ margin: "4px 0 0", fontSize: "12.5px", color: "var(--cf-text-muted)" }}>
            Corrispondenze "codice del fornitore → tuo codice" salvate per i template Import Ordini.
          </p>
        </div>
        <button className="cf-btn" onClick={() => navigate("/ordini")} style={{ padding: "8px 14px", borderRadius: "6px", border: "1px solid var(--cf-border-strong)", backgroundColor: "var(--cf-bg-card)", color: "var(--cf-text-primary)", fontSize: "13px", cursor: "pointer" }}>
          ← torna a Import Ordini
        </button>
      </div>

      <div style={{ display: "flex", gap: "10px", marginBottom: "14px", flexWrap: "wrap" }}>
        <input
          type="text"
          placeholder="🔍 filtra per fornitore, campo, codice..."
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          style={{ ...STILE_CAMPO, flex: "2 1 280px" }}
        />
        {eWtrendy && (
          <select value={azienda} onChange={(e) => setAzienda(e.target.value)} style={{ ...STILE_CAMPO, flex: "1 1 200px" }}>
            <option value="">Tutte le aziende</option>
            {aziendeNote.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
      </div>

      {errore && <p style={{ color: "var(--cf-danger-text)", fontSize: "13px" }}>{errore}</p>}

      {caricamento ? (
        <p style={{ color: "var(--cf-text-muted)", fontSize: "13px" }}>Caricamento...</p>
      ) : righe.length === 0 ? (
        <p style={{ color: "var(--cf-text-muted)", fontSize: "13px" }}>Nessuna conversione salvata ancora.</p>
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <span style={{ fontSize: "12.5px", color: "var(--cf-text-muted)" }}>
              {righeFiltrate.length} corrispondenze{testoFiltro ? ` (su ${righe.length} totali)` : ""}
              {selezionate.size > 0 ? ` — ${selezionate.size} selezionate` : ""}
            </span>
            <button
              onClick={eliminaSelezionate}
              disabled={!qualcunaSelezionata || eliminando}
              style={{
                padding: "7px 14px", borderRadius: "6px", border: "1px solid var(--cf-danger-text)",
                backgroundColor: qualcunaSelezionata ? "var(--cf-danger-text)" : "var(--cf-bg-card)",
                color: qualcunaSelezionata ? "#fff" : "var(--cf-danger-text)",
                fontSize: "12.5px", cursor: qualcunaSelezionata && !eliminando ? "pointer" : "not-allowed", fontWeight: 600,
              }}
            >
              🗑️ Elimina selezionate{selezionate.size > 0 ? ` (${selezionate.size})` : ""}
            </button>
          </div>

          <div style={{ maxHeight: "62vh", overflowY: "auto", border: "1px solid var(--cf-border)", borderRadius: "8px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ position: "sticky", top: 0, backgroundColor: "var(--cf-bg-card)", zIndex: 1 }}>
                  <th style={{ padding: "8px 10px", borderBottom: "1px solid var(--cf-border)", width: "34px" }}>
                    <input type="checkbox" checked={tutteSelezionate} onChange={toggleTutte} title="Seleziona/deseleziona tutte le righe filtrate" />
                  </th>
                  <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--cf-border)" }}>Fornitore</th>
                  <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--cf-border)" }}>Campo</th>
                  <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--cf-border)" }}>Codice fornitore</th>
                  <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--cf-border)" }}>Tuo codice</th>
                  <th style={{ padding: "8px 10px", borderBottom: "1px solid var(--cf-border)" }}></th>
                </tr>
              </thead>
              <tbody>
                {righeFiltrate.map((r) => (
                  <tr key={r.chiave} style={selezionate.has(r.chiave) ? { backgroundColor: "var(--cf-bg-subtle)" } : undefined}>
                    <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--cf-border)", textAlign: "center" }}>
                      <input type="checkbox" checked={selezionate.has(r.chiave)} onChange={() => toggleRiga(r.chiave)} />
                    </td>
                    <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--cf-border)" }}>{r.cliente}</td>
                    <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--cf-border)", color: "var(--cf-text-muted)" }}>{r.campo}</td>
                    <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--cf-border)", wordBreak: "break-word" }}>{r.codice}</td>
                    <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--cf-border)", fontWeight: 600 }}>{r.mioCodice}</td>
                    <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--cf-border)", textAlign: "center" }}>
                      <button
                        onClick={() => eliminaUna(r)}
                        disabled={eliminando}
                        title="Togli questa corrispondenza"
                        style={{ border: "none", background: "none", cursor: eliminando ? "wait" : "pointer", color: "var(--cf-danger-text)" }}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {righeFiltrate.length === 0 && (
            <p style={{ color: "var(--cf-text-muted)", fontSize: "13px", marginTop: "8px" }}>Nessuna corrispondenza trovata con questo filtro.</p>
          )}
        </>
      )}
    </div>
  );
}
